import {apiGet, apiPost, apiPut, apiDelete, apiDownload} from './apiClient';

/**
 * API для работы с массовой загрузкой отправлений
 */

/**
 * Загрузить Excel файл
 * @param {File} file - файл для загрузки
 * @returns {Promise}
 */
export function uploadMassUploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);

    // Используем apiPost который автоматически добавит правильный базовый URL
    return apiPost('massupload/upload', formData);
}

/**
 * Получить список файлов пользователя
 * @returns {Promise}
 */
export function getMassUploadFiles() {
    return apiGet('massupload/files');
}

/**
 * Получить детали файла
 * @param {number} fileId - ID файла
 * @returns {Promise}
 */
export function getMassUploadFileDetails(fileId) {
    return apiGet(`massupload/files/${fileId}`);
}

/**
 * Обновить отправление
 * @param {number} parcelId - ID отправления
 * @param {object} updatedData - обновленные данные
 * @param {string} fieldName - Опциональное имя поля для валидации только этого поля (например, "senderAddress" или "recipientAddress")
 * @returns {Promise}
 */
export function updateMassUploadParcel(parcelId, updatedData, fieldName = null) {
    let url = `massupload/parcels/${parcelId}`;
    if (fieldName) {
        url += `?fieldName=${encodeURIComponent(fieldName)}`;
    }
    return apiPut(url, updatedData);
}

/**
 * Удалить отправления из файла
 * @param {number} fileId - ID файла
 * @param {number[]} parcelIds - массив ID отправлений
 * @returns {Promise}
 */
export function deleteMassUploadParcels(fileId, parcelIds) {
    return apiDelete(`massupload/files/${fileId}/parcels`, {parcelIds});
}

/**
 * Удалить файл полностью
 * @param {number} fileId - ID файла
 * @returns {Promise}
 */
export function deleteMassUploadFile(fileId) {
    return apiDelete(`massupload/files/${fileId}`);
}

/**
 * Скачать исходный файл массовой загрузки
 * @param {number} fileId - ID файла
 * @returns {Promise}
 */
export function downloadMassUploadSourceFile(fileId) {
    return apiGet(`massupload/files/${fileId}/source`)
        .then(data => {
            const apiBaseUrl = window.getApiBaseUrl();
            const fileUrl = apiBaseUrl + 'files/' + data.result;
            window.location.href = fileUrl;
        })
        .catch(err => {
            console.error('Failed to download mass upload source file:', err);
            throw err;
        });
}

/**
 * Создать отправления из файла
 * @param {number} fileId - ID файла
 * @returns {Promise}
 */
export function createParcelsFromFile(fileId) {
    return apiPost(`massupload/files/${fileId}/create`, {});
}

/**
 * Применить исправление адреса ко всем строкам файла с таким же адресом
 * @param {number} fileId - ID файла
 * @param {object} request - параметры исправления
 * @returns {Promise}
 */
export function applyMassUploadAddressCorrection(fileId, request) {
    return apiPut(`massupload/files/${fileId}/parcels/apply-address-correction`, request);
}

/**
 * Скачать шаблон Excel
 * @returns {Promise}
 */
export function downloadMassUploadTemplate() {
    return apiGet('massupload/template')
        .then(data => {
            const apiBaseUrl = window.getApiBaseUrl();
            const fileUrl = apiBaseUrl + 'files/' + data.result;
            window.location.href = fileUrl;
        })
        .catch(err => {
            console.error('Failed to download template:', err);
            throw err;
        });
}

/**
 * Скачать шаблон массового расчета (заглушка)
 * @returns {Promise}
 */
export function downloadMassCalculationTemplate() {
    return apiGet('massupload/calculation/template')
        .then(data => {
            const apiBaseUrl = window.getApiBaseUrl();
            const fileUrl = apiBaseUrl + 'files/' + data.result;
            window.location.href = fileUrl;
        })
        .catch(err => {
            console.error('Failed to download mass calculation template:', err);
            throw err;
        });
}

/**
 * Загрузить Excel файл массового расчета
 * @param {File} file - файл для загрузки
 * @returns {Promise}
 */
export function uploadMassCalculationFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    return apiPost('massupload/calculation/upload', formData);
}

/**
 * Проверить доступность массового расчета
 * @returns {Promise<{error?: string, warning?: string}>}
 */
export function validateMassCalculationAvailability() {
    return apiGet('massupload/calculation/validate');
}

/**
 * Получить маршруты файла массового расчета
 * @param {number} fileId
 * @returns {Promise}
 */
export function getMassCalculationDetails(fileId) {
    return apiGet(`massupload/files/${fileId}/calculation`);
}

/**
 * Поставить все маршруты файла в очередь на расчет
 * @param {number} fileId
 * @returns {Promise}
 */
export function queueMassCalculation(fileId) {
    return apiPost(`massupload/files/${fileId}/calculation/request`, {});
}

/**
 * Отменить массовый расчет файла
 * @param {number} fileId
 * @returns {Promise}
 */
export function cancelMassCalculation(fileId) {
    return apiPost(`massupload/files/${fileId}/calculation/cancel`, {});
}

/**
 * Обновить адрес маршрута массового расчета
 * @param {number} routeId
 * @param {{fieldName: string, addressText: string, addressFromSuggestions: object}} payload
 * @returns {Promise}
 */
export function updateMassCalculationRouteAddress(routeId, payload) {
    return apiPut(`massupload/calculation/routes/${routeId}`, payload);
}

/**
 * Применить исправление адреса ко всем маршрутам файла с таким же исходным ошибочным адресом
 * @param {number} fileId
 * @param {object} request
 * @returns {Promise}
 */
export function applyMassCalculationAddressCorrection(fileId, request) {
    return apiPut(`massupload/files/${fileId}/calculation/routes/apply-address-correction`, request);
}

/**
 * Удалить выбранные маршруты массового расчета
 * @param {number} fileId
 * @param {number[]} routeIds
 * @returns {Promise}
 */
export function deleteMassCalculationRoutes(fileId, routeIds) {
    return apiDelete(`massupload/files/${fileId}/calculation/routes`, { ids: routeIds });
}

/**
 * Скачать итоговый файл массового расчета
 * @param {number} fileId
 * @returns {Promise}
 */
export function downloadMassCalculationResults(fileId) {
    return apiGet(`massupload/files/${fileId}/calculation/results`)
        .then(data => {
            const apiBaseUrl = window.getApiBaseUrl();
            const fileUrl = apiBaseUrl + 'files/' + data.result;
            window.location.href = fileUrl;
        })
        .catch(err => {
            console.error('Failed to download mass calculation results:', err);
            throw err;
        });
}




// WEBPACK FOOTER //
// ./App/services/massUploadApi.js